import { z } from "zod";

export const severitySchema = z.enum([
  "SEVERITY_UNSPECIFIED",
  "SEVERITY_INFO",
  "SEVERITY_WARNING",
  "SEVERITY_ERROR",
]);

export const audienceSchema = z.enum([
  "AUDIENCE_UNSPECIFIED",
  "AUDIENCE_USER",
  "AUDIENCE_ADMIN",
  "AUDIENCE_STAFF",
]);

export const scopeKindSchema = z.enum([
  "SCOPE_KIND_UNSPECIFIED",
  "SCOPE_KIND_SITE",
  "SCOPE_KIND_ORG",
  "SCOPE_KIND_GLOBAL",
]);

export type WireSeverity = z.infer<typeof severitySchema>;
export type WireAudience = z.infer<typeof audienceSchema>;
export type WireScopeKind = z.infer<typeof scopeKindSchema>;

export type Severity = "info" | "warning" | "error";

const SEVERITY_BY_WIRE: Record<WireSeverity, Severity> = {
  SEVERITY_UNSPECIFIED: "info",
  SEVERITY_INFO: "info",
  SEVERITY_WARNING: "warning",
  SEVERITY_ERROR: "error",
};

export const toSeverity = (wire: WireSeverity): Severity =>
  SEVERITY_BY_WIRE[wire];

export const scopeSchema = z.object({
  kind: scopeKindSchema,
  id: z.string().default(""),
});

const spanSchema = z.object({
  text: z.string().min(1),
  bold: z.boolean().default(false),
  href: z.string().default(""),
});

const kvItemSchema = z.object({
  key: z.string().min(1),
  value: z.string().default(""),
});

const tableRowSchema = z.object({
  cells: z.array(z.string()).default([]),
});

const actionSchema = z.object({
  label: z.string().min(1),
  url: z.string().min(1),
});

const textBlockSchema = z.object({
  type: z.literal("text"),
  spans: z.array(spanSchema).min(1),
});

const kvBlockSchema = z.object({
  type: z.literal("kv"),
  items: z.array(kvItemSchema).min(1),
});

const tableBlockSchema = z.object({
  type: z.literal("table"),
  columns: z.array(z.string()).min(1),
  rows: z.array(tableRowSchema).default([]),
});

const imageBlockSchema = z.object({
  type: z.literal("image"),
  url: z.string().min(1),
  alt: z.string().default(""),
});

const actionsBlockSchema = z.object({
  type: z.literal("actions"),
  items: z.array(actionSchema).min(1),
});

export const notificationBlockSchema = z.discriminatedUnion("type", [
  textBlockSchema,
  kvBlockSchema,
  tableBlockSchema,
  imageBlockSchema,
  actionsBlockSchema,
]);

export type NotificationBlock = z.infer<typeof notificationBlockSchema>;
export type TextBlock = z.infer<typeof textBlockSchema>;
export type KvBlock = z.infer<typeof kvBlockSchema>;
export type TableBlock = z.infer<typeof tableBlockSchema>;
export type ImageBlock = z.infer<typeof imageBlockSchema>;
export type ActionsBlock = z.infer<typeof actionsBlockSchema>;

const BLOCK_KEYS = ["text", "kv", "table", "image", "actions"] as const;

type BlockKey = (typeof BLOCK_KEYS)[number];

function tagWireBlock(wire: unknown): unknown {
  if (typeof wire !== "object" || wire === null) return wire;
  const record: Record<string, unknown> = { ...wire };
  const key = BLOCK_KEYS.find((candidate) => candidate in record);
  if (key === undefined) return wire;
  const body = record[key];
  if (typeof body !== "object" || body === null) return wire;
  return { type: key satisfies BlockKey, ...body };
}

export function parseNotificationBlocks(input: unknown): NotificationBlock[] {
  if (!Array.isArray(input)) return [];
  return input.flatMap((wire) => {
    const parsed = notificationBlockSchema.safeParse(tagWireBlock(wire));
    return parsed.success ? [parsed.data] : [];
  });
}

export const notificationSchema = z.object({
  schema_version: z.number().default(1),
  event_id: z.string(),
  scope: scopeSchema,
  audience: z.array(audienceSchema).default([]),
  type: z.string(),
  subtype: z.string().default(""),
  severity: severitySchema,
  required: z.boolean().default(false),
  occurred_at: z.string(),
  title: z.string(),
  blocks: z.unknown().transform(parseNotificationBlocks),
  producer: z.string().default(""),
});

export type Notification = z.infer<typeof notificationSchema>;

export const notificationRowSchema = z
  .object({
    id: z.string(),
    notification: notificationSchema,
    read_at: z.string().nullish(),
    created_at: z.string().nullish(),
    brand_name: z.string().nullish(),
  })
  .transform(({ id, notification, read_at, created_at, brand_name }) => ({
    id,
    event_id: notification.event_id,
    scope: notification.scope,
    audience: notification.audience,
    severity: notification.severity,
    required: notification.required,
    type: notification.type,
    subtype: notification.subtype,
    title: notification.title,
    blocks: notification.blocks,
    occurred_at: notification.occurred_at,
    created_at: created_at ?? "",
    read_at: read_at ?? null,
    brand_name: brand_name ?? "",
  }));

export type NotificationRow = z.infer<typeof notificationRowSchema>;
